<template>
    <div v-if="editor" class="tiptap">
        <!-- Modals -->
        <ImageExtensionModal
            v-model="showImageModal"
            :type="'image'"
            @save="insertImage"
            @hide="showImageModal = false"
        ></ImageExtensionModal>
        <EmbedExtensionModal
            v-model="showEmbedModal"
            :type="'embed'"
            @save="insertEmbed"
            @hide="showEmbedModal = false"
        ></EmbedExtensionModal>
        <LinkExtensionModal
            v-model="showLinkModal"
            @save="insertLink"
            @hide="showLinkModal = false"
        />

        <!-- Editor Menu -->
        <EditorMenuBar
            :editor="editor"
            v-slot="{ commands, isActive, getMarkAttrs, focused }"
        >
            <div class="menubar mb-2">
                <!-- HTML / Text Btns -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-button
                        :variant="editingHtml ? 'success' : ''"
                        v-b-popover.hover.top="'Edit HTML'"
                        @click="editHtml"
                    >
                        <font-awesome-icon :icon="['far', 'code']" />
                    </b-button>

                </b-button-group>

                <!-- Bold / Italic Marks -->
                <b-button-group :size="btnSize">
                    <b-button @click="commands.bold" :variant="isActive.bold() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'bold']" />
                    </b-button>

                    <b-button @click="commands.italic" :variant="isActive.italic() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'italic']" />
                    </b-button>

                    <b-button @click="commands.strike" :variant="isActive.strike() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'strikethrough']" />
                    </b-button>

                    <b-button @click="commands.underline" :variant="isActive.underline() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'underline']" />
                    </b-button>

                    <b-popover custom-class="p-0 font-popover" :target="`${_uid}_font_size_btn`" triggers="click" placement="top">
                        <FontSizePicker @save="commands.fontSize({fontSize: `${arguments[0]}px`})"/>
                    </b-popover>
                    <b-button :id="`${_uid}_font_size_btn`">
                        <font-awesome-icon :icon="['fas', 'text-size']" />
                    </b-button>

<!--                    <b-dropdown>-->
<!---->
<!--                    </b-dropdown>-->
                </b-button-group>

                <!-- Font Marks -->
                <b-button-group :size="btnSize">
                    <b-popover custom-class="p-0 font-popover" :target="`${_uid}_font_color_btn`" triggers="click" placement="top">
                        <ColorPicker @save="commands.color({color: arguments[0]})" />
                    </b-popover>
                    <b-btn :id="`${_uid}_font_color_btn`"
                           :variant="isActive.color() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'font']" />
                    </b-btn>
                    <b-popover custom-class="p-0 font-popover" :target="`${_uid}_font_highlight_btn`" triggers="click" placement="top">
                        <ColorPicker @save="commands.highlight({backgroundColor: arguments[0]})" />
                    </b-popover>
                    <b-btn :id="`${_uid}_font_highlight_btn`">
                        <font-awesome-icon :icon="['fas', 'highlighter']" />
                    </b-btn>
                </b-button-group>

                <!-- Align Marks -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-btn :variant="isActive.paragraph({textAlign: 'left'}) ? 'success' :''"
                           @click="commands.paragraph({ textAlign: 'left' })">
                        <font-awesome-icon :icon="['fas', 'align-left']" />
                    </b-btn>
                    <b-btn :variant="isActive.paragraph({textAlign: 'center'}) ? 'success' :''"
                           @click="commands.paragraph({ textAlign: 'center' })">
                        <font-awesome-icon :icon="['fas', 'align-center']" />
                    </b-btn>
                    <b-btn :variant="isActive.paragraph({textAlign: 'right'}) ? 'success' :''"
                           @click="commands.paragraph({ textAlign: 'right' })">
                        <font-awesome-icon :icon="['fas', 'align-right']" />
                    </b-btn>
                </b-button-group>

                <!-- Node Types (List/Bullet) -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-button @click="commands.bullet_list" :variant="isActive.bullet_list() ? 'success' :''">
                        <font-awesome-icon :icon="['far', 'list-ul']" />
                    </b-button>
                    <b-button @click="commands.ordered_list" :variant="isActive.ordered_list() ? 'success' :''">
                        <font-awesome-icon :icon="['far', 'list-ol']" />
                    </b-button>
                </b-button-group>

                <!-- Image / Link Nodes -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-button @click="addLink(commands.link, getMarkAttrs)" :variant="isActive.link() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'link']" />
                    </b-button>
                    <b-button @click="commands.link({href: null})" v-show="isActive.link() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'unlink']" />
                    </b-button>
                    <b-button @click="addImage(commands.image)">
                        <font-awesome-icon :icon="['far', 'image']" />
                    </b-button>
                    <b-button @click="addEmbed(commands.embed)">
                        IFrame
                        <font-awesome-icon :icon="['fat', 'code-simple']" />
                    </b-button>
                </b-button-group>

                <!-- Video -->
            </div>
        </EditorMenuBar>

        <div v-if="!editingHtml">
            <editor-content :editor="editor"/>
        </div>
        <div v-else>
            <prism-editor class="prism_editor"
                          v-model="html"
                          :code="html"
                          language="html"
                          :insertSpaces="false"
                          line-numbers
                          :highlight="highlighter"/>
        </div>
    </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble, Text } from 'tiptap'
import {
    OrderedList,
    BulletList,
    ListItem,
    Bold,
    Image,
    Italic,
    Link,
    Strike,
    Underline,
    History,
    Table,
    TableHeader,
    TableCell,
    TableRow,
    Heading,
    Placeholder,
} from 'tiptap-extensions'
import EmbedExtension from "@/components/tiptap/extensions/Nodes/embed_extension.js";
import EmbedExtensionModal from "@/components/tiptap/extensions/EmbedExtensionModal";

import ImageExtension from "@/components/tiptap/extensions/Nodes/image_extension.js";
import ImageExtensionModal from "@/components/tiptap/extensions/ImageExtensionModal";
import ParagraphExtension from "@/components/tiptap/extensions/Nodes/paragraph_extension";
import LinkExtensionModal from "@/components/tiptap/extensions/LinkExtensionModal";
import TextColorExtension from "@/components/tiptap/extensions/Marks/textcolor_extension";
import TextHighlightExtension from "@/components/tiptap/extensions/Marks/texthighlight_extension";
import FontSizeExtension from "@/components/tiptap/extensions/Marks/fontsize_extension";

import ColorPicker from "@/components/tiptap/extensions/ColorPicker";
import FontSizePicker from "@/components/tiptap/extensions/FontSizePicker";

import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css'; // import the styles somewhere
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/themes/prism.css';


export default {
    name: "TipTapEditor",
    components: {
        FontSizePicker,
        ColorPicker,
        LinkExtensionModal,
        ImageExtensionModal,
        EmbedExtensionModal,
        EditorContent,
        EditorMenuBar,
        EditorMenuBubble,
        PrismEditor,
    },

    props: {
        value: {
            type: String,
            default: '',
        },
        simple: {
            type: Boolean,
            default: false,
        }
    },

    computed: {
        btnSize() {
            if (this.simple)
                return 'sm';
            return 'sm';
        }
    },

    data() {
        return {
            editor: null,
            html: null,
            editingHtml: false,
            showImageModal: false,
            showEmbedModal: false,
            showLinkModal: false,
            currentLink: {
                url: null,
            },
            customColor: null,
            imageSettings: {
                command: null,
                src: null,
                width: null,
                height: null,
            },
            embedSettings: {
                command: null,
                src: null,
                width: null,
                height: null,
            },
            linkSettings: {
                command: null,
                target: '_blank',
                getMarkAttrs: null,
            }
        }
    },

    watch: {
        value(value) {
            // HTML
            const isSame = this.editor.getHTML() === value
            if (isSame) {
                return
            }
            this.editor.setContent(this.value, false)
        },
        html(html) {
            this.$emit('input', html);
        },
    },

    mounted() {
        this.editor = new Editor({
            extensions: [
                new Placeholder(),
                new BulletList(),
                new OrderedList(),
                new ListItem(),
                new Bold(),
                // new Image(),
                new Italic(),
                new Link(),
                new Strike(),
                new Underline(),
                new History(),
                new Table({
                    resizable: true,
                }),
                new TableHeader(),
                new TableCell(),
                new TableRow(),
                new ImageExtension(),
                new EmbedExtension(),
                new ParagraphExtension(),
                new TextColorExtension(),
                new TextHighlightExtension(),
                new FontSizeExtension(),
            ],
            content: this.value,
            onUpdate: (value) => {
                let html = null

                if (value) {
                    html = value.getHTML()
                }

                if (html === '<p></p>') {
                    html = ''
                }

                this.$emit('input', html)

                // this.pending_autosave = true
                //
                // this.autosave(value.getHTML())
            },
            onBlur: () => {
                if (this.changes) {
                    this.$emit('blur')
                }

                // this.pending_autosave = false

                // this.cached_value = _.cloneDeep(this.value)
            }

        })
    },

    beforeDestroy() {
        this.editor.destroy()
    },

    methods: {
        addImage(command) {
            this.imageSettings.command = command;
            // this.$bvModal.show('image_modal');
            this.showImageModal = true;
        },
        addEmbed(command) {
            this.embedSettings.command = command;
            // this.$bvModal.show('image_modal');
            this.showEmbedModal = true;
        },
        insertEmbed({src, width, height, align, alt, caption, credit}) {
            if (width)
                width = `${width}%`;

            this.editor.commands.embedNode({
                align: align,
                alt: alt ? alt: null,
                caption: caption ? caption: null,
                credit: credit ? credit: null,
                src: src,
                width: width ? width: null,
                height: height ? height: null,

            })

            this.$emit('update');
            this.showEmbedModal = false;
        },
        insertImage({src, width, align, alt, caption, credit}) {
            if (width)
                width = `${width}%`;

            this.editor.commands.imageNode({
                align: align,
                alt: alt ? alt: null,
                caption: caption ? caption: null,
                credit: credit ? credit: null,
                src: src,
                width: width ? width: null,
            })

            this.$emit('update');
            this.showImageModal = false;
        },
        addLink(command, getMarkAttrs) {
            this.linkSettings.command = command;
            this.linkSettings.getMarkAttrs = getMarkAttrs;
            this.showLinkModal = true;
        },
        insertLink({url}) {
            this.linkSettings.command({href: url});
            this.$emit('update');
            this.showLinkModal = false;
        },
        editHtml() {
            if (this.editingHtml) {
                this.editor.setContent(this.html);
                this.editingHtml = false;
                return;
            }

            this.editingHtml = true;
            this.html = this.editor.getHTML();
            console.log(this.html);
        },
        highlighter(code) {
            return highlight(code, languages.html, 'html');
        },
    }
}
</script>

<style lang="scss">

.ProseMirror {
    border-top: 1px solid black;
}

/* Placeholder (at the top) */
.ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-empty-text);
    float: left;
    color: #ced4da;
    pointer-events: none;
    height: 0;
}


.prism-editor {

}

.font-popover, .font-popover .popover-body {
    padding: 0;
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/_colors';

.tiptap {
     .editor-content {
     	 border: 1px solid #dee2e6;
     }
    // p.is-editor-empty:first-child::before {
    //     content: attr(data-empty-text);
    //     float: left;
    //     color: #bbb;
    //     pointer-events: none;
    //     height: 0;
    // }
    &.is-invalid {
        .editor-content {
            border: 1px solid $danger;
        }
    }
    .menububble {
        position: absolute;
        display: -webkit-box;
        display: flex;
        z-index: 20;
        background: #000;
        border-radius: 5px;
        padding: .3rem;
        margin-bottom: .5rem;
        -webkit-transform: translateX(-50%);
        transform: translateX(-50%);
        visibility: hidden;
        opacity: 0;
        -webkit-transition: opacity .2s,visibility .2s;
        transition: opacity .2s,visibility .2s;
        &.active {
            opacity: 1;
            visibility: visible;
        }
    }
    .menububble__button {
        display: -webkit-inline-box;
        display: inline-flex;
        background: transparent;
        border: 0;
        color: #fff;
        padding: .2rem .5rem;
        margin-right: .2rem;
        border-radius: 3px;
        cursor: pointer;
        &.active {
            background-color: hsla(0, 1%, 100%, .2);
        }
    }
}

.editor-content {
    &.form-control {
        padding: 0;
    }
}


.btn-group {
    &.readonly {
        pointer-events: none;
    }
}

.editor-content {
    &.readonly {
        cursor: text;
    }
}

.btn-group {
    margin-right: 10px;
}

@media (max-width: 767px) {
    .tiptap {
        .editor-menu-bar {
            .btn {
                padding: 0.25rem 0.5rem;
                font-size: 0.875rem;
                border-radius: 0.2rem;
            }
        }
    }
}


</style>